/**
 * PWA — banner de instalación (primera visita, móvil) + guía paso a paso (diálogo).
 * Android/Chrome: botón «Instalar» dispara el prompt nativo (si Chrome ya lo emitió) o muestra la guía del menú ⋮.
 * iPhone/Safari: 3 pasos «Compartir → Agregar a pantalla de inicio → Agregar».
 */
import { Share, SquarePlus, Download, X, Smartphone, EllipsisVertical, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { usePwaInstall, isSafari } from '@/pwa/PwaInstallContext';
import { tr } from '@/contexts/LanguageContext';

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3" data-testid={`pwa-step-${n}`}>
    <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{n}</span>
    <p className="text-sm text-foreground leading-snug">
      {children}
      {Icon && <Icon className="inline-block w-4 h-4 text-primary ml-1.5 -mt-0.5 align-middle" aria-hidden />}
    </p>
  </li>
);

export const InstallSteps = ({ ios }) => (
  <ol className="space-y-3" data-testid={ios ? 'pwa-steps-ios' : 'pwa-steps-android'}>
    {ios ? (
      <>
        <Step n={1} icon={Share}>{tr("Toca el botón")}{' '}<strong>{tr("Compartir")}</strong>{' '}{tr("de Safari (abajo, el cuadrado con la flecha)")}</Step>
        <Step n={2} icon={SquarePlus}>{tr("Elige")}{' '}<strong>{tr("«Agregar a pantalla de inicio»")}</strong></Step>
        <Step n={3} icon={Check}>{tr("Toca")}{' '}<strong>{tr("Agregar")}</strong>{tr(". Ziyup aparecerá como una app más en tu teléfono")}</Step>
      </>
    ) : (
      <>
        <Step n={1} icon={EllipsisVertical}>{tr("Abre el menú")}{' '}<strong>⋮</strong>{' '}{tr("de Chrome (arriba a la derecha)")}</Step>
        <Step n={2} icon={Download}>{tr("Toca")}{' '}<strong>{tr("«Instalar app»")}</strong> o <strong>{tr("«Agregar a pantalla de inicio»")}</strong></Step>
        <Step n={3} icon={Check}>{tr("Confirma con")}{' '}<strong>{tr("Instalar")}</strong>{tr(". Se abrirá a pantalla completa, sin barra del navegador")}</Step>
      </>
    )}
  </ol>
);

export const InstallGuideDialog = () => {
  const { guideOpen, closeGuide, ios, canPrompt, promptInstall, installed, standalone } = usePwaInstall();
  const onInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') { toast.success(tr("Ziyup se está instalando en tu teléfono")); closeGuide(); }
  };
  return (
    <Dialog open={guideOpen} onOpenChange={(o) => !o && closeGuide()}>
      <DialogContent className="max-w-sm bg-card border-border" data-testid="pwa-guide-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><Smartphone className="w-5 h-5 text-primary" />{' '}{tr("Instalar Ziyup")}</DialogTitle>
          <DialogDescription>
            {installed || standalone ? tr("Ya tienes Ziyup instalada en este dispositivo.") : tr("Ábrela a pantalla completa desde tu pantalla de inicio, sin pasar por la tienda de apps.")}
          </DialogDescription>
        </DialogHeader>
        {!(installed || standalone) && (
          <>
            {canPrompt ? (
              <Button onClick={onInstall} className="w-full rounded-full gap-2 font-bold" data-testid="pwa-guide-install-btn"><Download className="w-4 h-4" />{' '}{tr("Instalar ahora")}</Button>
            ) : (
              <InstallSteps ios={ios} />
            )}
            {!ios && !canPrompt && (
              <p className="text-[11px] text-muted-foreground" data-testid="pwa-guide-hint">{tr("Si no ves la opción, usa Chrome o Edge: los navegadores dentro de otras apps no permiten instalar.")}</p>
            )}
            {ios && !isSafari() && (
              <p className="text-[11px] text-amber-400" data-testid="pwa-guide-ios-hint">{tr("En iPhone solo Safari permite instalar: abre lineagetournaments.com en Safari y repite los pasos.")}</p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const InstallBanner = () => {
  const { bannerVisible, dismissBanner, openGuide, ios, canPrompt, promptInstall } = usePwaInstall();
  // Flujo OAuth del conector Claude (navegador interno): nada debe tapar el botón de autorizar.
  if (!bannerVisible || window.location.pathname.startsWith('/oauth/') || window.location.pathname.startsWith('/live/') || window.location.pathname.startsWith('/judge/onboarding')) return null;
  const primary = async () => {
    if (canPrompt) {
      const outcome = await promptInstall();
      if (outcome === 'accepted') { toast.success(tr("Ziyup se está instalando en tu teléfono")); dismissBanner(); }
      return;
    }
    openGuide();
  };
  return (
    <div className="fixed left-3 right-3 z-[70] bg-card/95 backdrop-blur-md border border-primary/40 rounded-2xl p-3 shadow-gold flex items-center gap-3"
      style={{ bottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }} role="dialog" aria-label={tr("Instalar Ziyup")} data-testid="pwa-install-banner">
      <img src="/icons/ziyup-app-icon-192.png?v=ziyup1" alt="" className="w-11 h-11 rounded-xl border border-border flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-foreground leading-tight">{tr("Instala Ziyup en tu teléfono")}</p>
        <p className="text-[11px] text-muted-foreground leading-snug" data-testid="pwa-install-banner-sub">
          {ios ? tr("Compartir → Agregar a pantalla de inicio. Abre a pantalla completa, como una app.") : tr("Gratis, sin tienda de apps. Abre a pantalla completa, como una app.")}
        </p>
      </div>
      <Button size="sm" onClick={primary} className="rounded-full h-8 px-3 text-xs font-bold gap-1 flex-shrink-0" data-testid="pwa-install-banner-btn">
        {ios ? tr("Cómo") : tr("Instalar")}
      </Button>
      <button type="button" onClick={dismissBanner} aria-label={tr("Cerrar")} className="text-muted-foreground hover:text-foreground p-1 -mr-1 flex-shrink-0" data-testid="pwa-install-banner-close">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
